/**
 * Splits one utterance into separate tries when the child said the word more than once ("rah …
 * rah") without a pause long enough to end the utterance. The endpointer keeps short pauses
 * inside an utterance (a syllable's hangover is nearly a second), so two tries often arrive as one
 * recording, or as one cut at the maximum length. Each try can then be scored on its own and the
 * best one judged, instead of asking for "just the one word".
 *
 * Only clearly separated tries are split: voiced bursts at a similar loudness (a quieter burst may
 * be background or the tail of an echo, so it is not a try) with a real pause between them (longer
 * than a stop closure inside a word, as in "rab-bit"), each word-length and with quiet before the
 * first and after the last. Anything else (one burst, talk, a try cut off at the end) is left
 * alone: a wrongly split sentence could otherwise be accepted for one of its syllables.
 */

const FRAME_MS = 10;
/** A pause at least this long separates two tries (a stop closure inside a word is shorter). */
export const MIN_GAP_MS = 300;
/**
 * A try is voiced for at least this long, and at most this long: one word or syllable, even
 * stretched ("rrrah"); longer is talk. Doubled real child words (PERCEPT-R) split 97% of the
 * time with these rules, runs of real talk never. In an iPhone QA session (2026-10-11) every cut
 * utterance was talk (1-1.3 s runs between short pauses, running on past the cut); none splits.
 */
const MIN_TRY_MS = 150;
const MAX_TRY_MS = 1500;
/** Tries of one word take a similar time; runs of talk differ more. */
const MAX_LENGTH_RATIO = 2;
/**
 * Quiet before the first try and after the last, so a try cut off at the maximum length (or talk
 * running on past it) is not taken for a whole one.
 */
const EDGE_QUIET_MS = 150;
/** Silence kept around each try (at most half the pause), so its onset and release are kept. */
const MARGIN_MS = 120;
/** Frames this far above the noise floor (and not too far below the peak) are voiced. */
const VOICE_ABOVE_NOISE_DB = 10;
const VOICE_BELOW_PEAK_DB = 25;
/** A try peaks within this much of the loudest try (the child, not something further away). */
const TRY_BELOW_PEAK_DB = 12;
/** More bursts than this is talk (a sentence), not repeated tries. */
export const MAX_TRIES = 3;

export interface Try {
  audio: Float32Array;
  startMs: number;
  endMs: number;
  peakDb: number;
}

const frameDb = (x: Float32Array) => {
  let sum = 0;
  for (const v of x) sum += v * v;
  return 10 * Math.log10(sum / Math.max(1, x.length) + 1e-12);
};

/**
 * The separate tries in `audio` (16 kHz unless given), or undefined when it does not hold two or
 * more clearly separated ones. `noiseDb` is the room's noise floor (dBFS, as the endpointer's).
 */
export function splitTries(
  audio: Float32Array,
  noiseDb: number,
  sampleRate = 16000,
): Try[] | undefined {
  const hop = Math.round((sampleRate * FRAME_MS) / 1000);
  const db: number[] = [];
  for (let i = 0; i + hop <= audio.length; i += hop) db.push(frameDb(audio.subarray(i, i + hop)));
  const peak = Math.max(...db);
  const threshold = Math.max(noiseDb + VOICE_ABOVE_NOISE_DB, peak - VOICE_BELOW_PEAK_DB);

  // Voiced runs, merged across pauses shorter than a gap between tries.
  const minGap = MIN_GAP_MS / FRAME_MS;
  const bursts: { start: number; end: number }[] = [];
  db.forEach((d, i) => {
    if (d < threshold) return;
    const last = bursts.at(-1);
    if (last && i - last.end < minGap) last.end = i + 1;
    else bursts.push({ start: i, end: i + 1 });
  });
  // Clicks and breaths (too short to be a try) are ignored.
  const sounds = bursts.filter((b) => (b.end - b.start) * FRAME_MS >= MIN_TRY_MS);
  const tries = sounds.filter(
    (b) => Math.max(...db.slice(b.start, b.end)) >= peak - TRY_BELOW_PEAK_DB,
  );
  // Only an utterance made of tries alone: a quieter voice among them (other talk) or many tries
  // (a sentence) is not a clean repeat.
  if (tries.length < 2 || tries.length > MAX_TRIES || tries.length !== sounds.length)
    return undefined;
  const lengths = tries.map((b) => (b.end - b.start) * FRAME_MS);
  if (Math.max(...lengths) > MAX_TRY_MS) return undefined;
  if (Math.max(...lengths) > MAX_LENGTH_RATIO * Math.min(...lengths)) return undefined;
  const edge = EDGE_QUIET_MS / FRAME_MS;
  if ((tries[0]?.start ?? 0) < edge || (tries.at(-1)?.end ?? db.length) > db.length - edge)
    return undefined;

  const margin = MARGIN_MS / FRAME_MS;
  return tries.map((b, i) => {
    const before = i > 0 ? (b.start - (tries[i - 1]?.end ?? 0)) / 2 : margin;
    const after = i < tries.length - 1 ? ((tries[i + 1]?.start ?? 0) - b.end) / 2 : margin;
    const start = Math.max(0, b.start - Math.min(margin, before));
    const end = Math.min(db.length, b.end + Math.min(margin, after));
    return {
      audio: audio.slice(Math.round(start * hop), Math.round(end * hop)),
      startMs: start * FRAME_MS,
      endMs: end * FRAME_MS,
      peakDb: Math.max(...db.slice(b.start, b.end)),
    };
  });
}
